import { useEffect, useRef, useState } from "react";
import { ReactComponent as LogoRRTV } from "../assets/logos/LOGO RR TV.svg";

// HLS stream from MediaMTX — override via env
const HLS_URL = process.env.REACT_APP_HLS_URL || "/api/rrtv/rrtv/index.m3u8";

/**
 * RRTVPlayer
 * variant = "sidebar"    — compact 16:9, replaces BreadButterStrip in sidebar
 * variant = "widescreen" — full-width 16:9, below Row3 in main column
 * variant = "inline"     — fills parent container (for RRTVMarketPage left zone)
 */
export function RRTVPlayer({ variant = "sidebar" }) {
  const videoRef  = useRef(null);
  const [status,    setStatus]    = useState("loading"); // loading | live | error
  const [hlsReady,  setHlsReady]  = useState(!!window.Hls);

  // Load hls.js once
  useEffect(() => {
    if (window.Hls) return;
    const s = document.createElement("script");
    s.src     = "https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js";
    s.onload  = () => setHlsReady(true);
    s.onerror = () => setStatus("error");
    document.head.appendChild(s);
  }, []);

  // Attach HLS
  useEffect(() => {
    if (!hlsReady || !videoRef.current) return;
    const Hls = window.Hls;
    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true, backBufferLength: 10 });
      hls.loadSource(HLS_URL);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setStatus("live");
        videoRef.current?.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_, d) => { if (d.fatal) setStatus("error"); });
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      videoRef.current.src = HLS_URL;
      videoRef.current.addEventListener("loadedmetadata", () => {
        setStatus("live");
        videoRef.current?.play().catch(() => {});
      });
    } else {
      setStatus("error");
    }
    return () => hls?.destroy();
  }, [hlsReady]);

  const v = VARIANTS[variant] || VARIANTS.sidebar;

  return (
    <>
      <style>{PULSE_CSS}</style>
      <div style={v.wrapper}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.brand}>
            <LogoRRTV style={{ height: 18, width: "auto", display: "block" }} />
          </div>
          <LiveBadge status={status} />
        </div>

        {/* ── Video ── */}
        <div style={{ ...S.videoWrap, ...(variant === "inline" ? { flex: 1, minHeight: 0 } : { aspectRatio: v.ratio }) }}>
          {status === "error" ? (
            <div style={S.offline}>
              <span style={{ fontSize: 28 }}>📡</span>
              <span style={S.offlineText}>SINAL OFFLINE</span>
              <span style={S.offlineSub}>stream não disponível</span>
            </div>
          ) : (
            <>
              <video ref={videoRef} style={S.video} muted playsInline autoPlay />
              {status === "loading" && (
                <div style={S.overlay}>
                  <span style={S.dot} />
                  <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: "#a1a1aa", fontSize: 11, letterSpacing: "0.08em" }}>
                    conectando...
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer schedule pill (sidebar + widescreen only) ── */}
        {variant !== "inline" && (
          <div style={S.footer}>
            <span style={S.footerNext}>PRÓXIMO</span>
            <span style={S.footerShow}>RR Fechamento · 17h30</span>
          </div>
        )}
      </div>
    </>
  );
}

function LiveBadge({ status }) {
  if (status === "live")    return <div style={S.badgeLive}><span style={S.badgeDot} />AO VIVO</div>;
  if (status === "error")   return <div style={S.badgeOff}>OFFLINE</div>;
  return <div style={S.badgeWait}>AGUARDE</div>;
}

// ── Variant configs ─────────────────────────────────────────────
const VARIANTS = {
  sidebar: {
    ratio: "16/9",
    wrapper: {
      background: "#18181b",
      border: "1px solid #27272a",
      borderRadius: 6,
      overflow: "hidden",
      marginBottom: 16,
    },
  },
  widescreen: {
    ratio: "16/9",
    wrapper: {
      background: "#18181b",
      border: "1px solid #27272a",
      borderRadius: 6,
      overflow: "hidden",
      width: "100%",
      marginTop: 20,
    },
  },
  inline: {
    ratio: "unset",
    // flex column so the video fills the container's HEIGHT (not just width),
    // framing correctly at any desktop resolution without zooming.
    wrapper: { width: "100%", height: "100%", background: "#000", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },
  },
};

// ── Shared styles ───────────────────────────────────────────────
const S = {
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px", borderBottom: "1px solid #27272a",
  },
  brand:  { display: "flex", alignItems: "center", gap: 8 },
  videoWrap: {
    position: "relative", width: "100%", background: "#000",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  video:  { width: "100%", height: "100%", display: "block", objectFit: "cover" },
  overlay: {
    position: "absolute", inset: 0,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 8,
    background: "rgba(0,0,0,0.55)",
  },
  dot:    { width: 8, height: 8, borderRadius: "50%", background: "#6660fe", animation: "rrtv-pulse 1s infinite" },
  offline: {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    gap: 6, padding: 32, width: "100%",
  },
  offlineText: { fontFamily: "'IBM Plex Mono', monospace", color: "#71717a", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em" },
  offlineSub:  { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: "#52525b", fontSize: 10 },
  badgeLive: {
    display: "flex", alignItems: "center", gap: 5,
    fontFamily: "'IBM Plex Mono', monospace",
    background: "rgba(220,38,38,.15)", color: "#f87171",
    fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
    padding: "3px 8px", borderRadius: 3,
  },
  badgeDot: { width: 6, height: 6, borderRadius: "50%", background: "#ef4444", animation: "rrtv-pulse 1s infinite" },
  badgeOff:  { fontFamily: "'IBM Plex Mono', monospace", background: "rgba(39,39,42,.8)", color: "#71717a", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 3 },
  badgeWait: { fontFamily: "'IBM Plex Mono', monospace", background: "rgba(245,158,11,.1)", color: "#fbbf24", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "3px 8px", borderRadius: 3 },
  footer:     { display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderTop: "1px solid #27272a" },
  footerNext: { fontFamily: "'IBM Plex Mono', monospace", color: "#f59e0b", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em" },
  footerShow: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: "#a1a1aa", fontSize: 11 },
};

const PULSE_CSS = `
  @keyframes rrtv-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
`;